import React, { useState, useEffect, useCallback } from "react";
import { Box, Button, IconButton, Modal, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const EditHotelBrand = ({ open, handleClose, orderData, handleSave }) => {
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    address: "",
    phone: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (orderData) {
      setFormData({
        id: orderData.id || "",
        name: orderData.name || "",
        address: orderData.address || "",
        phone: orderData.phone || "",
      });
    }
  }, [orderData]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({ ...prevData, [name]: value }));
  }, []);

  const validate = useCallback(() => {
    const newErrors = {};
    const { name, address, phone } = formData;

    if (!name.trim()) newErrors.name = "Name is required";
    if (!address.trim()) newErrors.address = "Address is required";
    
    const phoneNumberPattern = /^[0-9]{10}$/;
    if (!phone.trim()) {
      newErrors.phone = "Phone is required";
    } else if (!phoneNumberPattern.test(phone.trim())) {
      newErrors.phone = "Phone number must be 10 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Memoized submit handler
  const handleSubmit = useCallback(() => {
    if (validate()) {
      handleSave(formData);
      handleClose();
    }
  }, [validate, formData, handleSave, handleClose]);

  // Phone input validation handler
  const handlePhoneKeyPress = useCallback((e) => {
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  }, []);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={webStyle.style}>
        <Box sx={webStyle.header}>
          <Typography variant="h6" component="h2" sx={webStyle.modalTitle}>
            Edit Hotel Brand
          </Typography>
          <IconButton onClick={handleClose} sx={webStyle.closeButton}>
            <CloseIcon />
          </IconButton>
        </Box>

        <TextField
          label="Name"
          variant="outlined"
          name="name"
          value={formData.name}
          error={!!errors.name}
          helperText={errors.name}
          onChange={handleChange}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Address"
          variant="outlined"
          name="address"
          value={formData.address}
          error={!!errors.address}
          helperText={errors.address}
          onChange={handleChange}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Phone"
          variant="outlined"
          name="phone"
          value={formData.phone}
          error={!!errors.phone}
          helperText={errors.phone}
          onChange={handleChange}
          fullWidth
          margin="normal"
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            maxLength: 10,
          }}
          onKeyPress={handlePhoneKeyPress}
        />
        
        <Box sx={webStyle.actions}>
          <Button variant="contained" sx={webStyle.cancelBtn} onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" sx={webStyle.saveBtn} onClick={handleSubmit}>
            Save
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditHotelBrand;

// Styles consolidated and optimized
const webStyle = {
  style: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxWidth: 650,
    width: "90%",
    bgcolor: "background.paper",
    borderRadius: 1,
    boxShadow: 24,
    p: { xs: "20px", sm: "34px" },
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: "20px",
  },
  modalTitle: {
    color: '#00A651',
  },
  closeButton: {
    // No additional styles needed beyond default
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    mt: 2,
  },
  cancelBtn: {
    backgroundColor: "white",
    border: "1px solid green",
    color: "green !important",
    '&:hover': {
      backgroundColor: "white",
      color: "green",
    },
  },
  saveBtn: {
    backgroundColor: "green",
    border: "1px solid green",
    color: "white",
    '&:hover': {
      backgroundColor: "green",
    },
  },
};